import { useState, useEffect, useRef, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster, toast } from "sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import NewProject from "@/pages/NewProject";
import ProjectView from "@/pages/ProjectView";
import Contact from "@/pages/Contact";
import Inbox from "@/pages/Inbox";
import Compare from "@/pages/Compare";
import ChargerCatalog from "@/pages/ChargerCatalog";
import GuestDesigner from "@/pages/GuestDesigner";
import Planner from "@/pages/Planner";
import ROICalculator from "@/pages/ROICalculator";
import Vendors from "@/pages/Vendors";
import SiteSelection from "@/pages/SiteSelection";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import BlogList from "@/pages/BlogList";
import BlogPost from "@/pages/BlogPost";

// Backend API base URL:
// IMPORTANT: For custom domains (e.g., https://evhubstudio.com), using a different backend origin
// (e.g., *.emergent.host) turns auth cookies into third-party cookies which many browsers block.
// So we prefer same-origin unless REACT_APP_BACKEND_URL matches the current host.
const ENV_BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const BACKEND_URL = ENV_BACKEND_URL || (process.env.NODE_ENV === "development" ? "http://localhost:8000" : window.location.origin);

export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: "include",
      });
      if (response.ok) {
        const userData = await response.json();
        if (userData?.authenticated) {
          setUser(userData);
        } else {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async () => {
    try {
      const resp = await fetch(`${API}/auth/google`);
      const data = await resp.json();
      window.location.href = data.url;
    } catch (e) {
      toast.error("Failed to start sign in. Please try again.");
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
      setUser(null);
      toast.success("Logged out successfully");
    } catch (error) {
      toast.error("Logout failed");
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash || "";
      const search = location.search || "";
      const sessionIdMatch = hash.match(/session_id=([^&]+)/) || search.match(/session_id=([^&]+)/);

      if (sessionIdMatch) {
        const sessionId = decodeURIComponent(sessionIdMatch[1]);
        try {
          const response = await fetch(`${API}/auth/session`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ session_id: sessionId }),
            credentials: "include",
          });
          
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            toast.success(`Welcome, ${userData.name}!`);
            navigate("/dashboard", { replace: true, state: { user: userData } });
          } else {
            toast.error("Authentication failed");
            navigate("/", { replace: true });
          }
        } catch (error) {
          toast.error("Authentication error. Please try again.");
          navigate("/", { replace: true });
        }
      } else {
        navigate("/", { replace: true });
      }
    };

    processAuth();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="auth-callback">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
        <p className="mt-4 text-slate-600">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Allow react-snap to prerender protected pages without a redirect
  if (navigator.userAgent === "ReactSnap") return <>{children}</>;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment OR querystring for session_id SYNCHRONOUSLY during render
  if (location.hash?.includes("session_id=") || location.search?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/try" element={<GuestDesigner />} />
      <Route path="/contact" element={<Contact />} />
      <Route
        path="/inbox"
        element={
          <ProtectedRoute>
            <Inbox />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/new-project"
        element={
          <ProtectedRoute>
            <NewProject />
          </ProtectedRoute>
        }
      />
      <Route
        path="/project/:projectId"
        element={
          <ProtectedRoute>
            <ProjectView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/compare"
        element={
          <ProtectedRoute>
            <Compare />
          </ProtectedRoute>
        }
      />
      <Route path="/charger-catalog" element={<ChargerCatalog />} />
      <Route path="/planner" element={<Planner />} />
      <Route path="/roi-calculator" element={<ROICalculator />} />
      <Route path="/vendors" element={<Vendors />} />
      <Route path="/site-selection" element={<SiteSelection />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/blog" element={<BlogList />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
    </Routes>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
